import { gql } from '@apollo/client';
import {
  Button,
  chakra,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useToast,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { useDestructiveAction } from '~components/ui/DestructiveAction';
import { LoginIcon, MoreIconVertical, SuccessIcon } from '~components/ui/icons';
import {
  ServiceAdminOrgAccessRequestDecision,
  ServiceAdminOrgAccessRequestFilter,
  ServiceAdminOrgAccessRequestStatus,
} from '~graphql/__generated__/types';
import { HandleApiError } from '~graphql/HandleApiError';
import { fromError } from '~utils/errors';
import {
  ReceivedAccessRequestsQuery,
  useDecideOrgAdminAccessRequestMutation,
  useReceivedAccessRequestsQuery,
  useRevokeOrgAdminAccessRequestMutation,
} from './__generated__/ReceivedAccessRequests.graphql';
import { AccessRequestColumn, AccessRequestsSection, TruncatedCell } from './AccessRequestsSection';
import { formatDurationMinutes, formatExpiryDate, getEffectiveStatus } from './accessRequestUtils';

type ReceivedAccessRequest = ReceivedAccessRequestsQuery['organizationAccessRequests'][number] & {
  requesterName: string;
  requesterEmail: string;
};

export function ReceivedAccessRequests() {
  const [filter, setFilter] = useState(ServiceAdminOrgAccessRequestFilter.All);
  const { data, loading, error } = useReceivedAccessRequestsQuery({
    variables: { filter },
    fetchPolicy: 'cache-and-network',
  });

  const rows = useMemo<ReceivedAccessRequest[]>(
    () =>
      (data?.organizationAccessRequests ?? []).map((request) => {
        const requester = request.requestingServiceAdmin;
        const name = [requester?.givenName, requester?.familyName].filter(Boolean).join(' ');

        return {
          ...request,
          requesterName: name || '-',
          requesterEmail: requester?.email ?? '-',
        };
      }),
    [data],
  );

  const columns = useMemo<Array<AccessRequestColumn<ReceivedAccessRequest>>>(
    () => [
      {
        id: 'name',
        header: t('name'),
        render: (request) => (
          <TruncatedCell text={request.requesterName} maxWidth="48" fontWeight="semibold" />
        ),
      },
      {
        id: 'email',
        header: t('email'),
        render: (request) => <TruncatedCell text={request.requesterEmail} maxWidth="64" />,
      },
      { id: 'expiry', header: t('requestAccessExpiry'), render: formatExpiryDate },
      {
        id: 'accessFor',
        header: t('accessFor'),
        render: (request) => formatDurationMinutes(request.requestedDurationMinutes),
      },
      {
        id: 'status',
        header: '',
        render: (request) => <ReceivedRequestStatus request={request} />,
      },
      {
        id: 'actions',
        header: '',
        width: '16',
        render: (request) => <ReceivedRequestActions request={request} />,
      },
    ],
    [],
  );

  if (error) {
    return <HandleApiError error={error} />;
  }

  return (
    <AccessRequestsSection
      icon={LoginIcon}
      title={t('receivedRequests')}
      description={t('receivedRequestsDescription')}
      filter={filter}
      onFilterChange={setFilter}
      isLoading={loading && !data}
      rows={rows}
      columns={columns}
    />
  );
}

function ReceivedRequestStatus({ request }: { request: ReceivedAccessRequest }) {
  switch (getEffectiveStatus(request)) {
    case ServiceAdminOrgAccessRequestStatus.Awaiting:
      return <DecisionButtons requestId={request.id} />;
    case ServiceAdminOrgAccessRequestStatus.Approved:
      return (
        <chakra.span color="green.500" fontWeight="medium">
          {t('accepted')}
        </chakra.span>
      );
    case ServiceAdminOrgAccessRequestStatus.Rejected:
      return (
        <chakra.span color="red.500" fontWeight="medium">
          {t('declined')}
        </chakra.span>
      );
    case ServiceAdminOrgAccessRequestStatus.Revoked:
      return (
        <chakra.span color="red.500" fontWeight="medium">
          {t('revoked')}
        </chakra.span>
      );
    default:
      return (
        <chakra.span color="gray.400" fontWeight="medium">
          {t('expiredStatus')}
        </chakra.span>
      );
  }
}

function DecisionButtons({ requestId }: { requestId: string }) {
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const [decide, { loading }] = useDecideOrgAdminAccessRequestMutation({
    refetchQueries: ['ReceivedAccessRequests'],
    awaitRefetchQueries: true,
  });

  if (!verifyUserPermissions([Permission.ServiceAdminOrgAccessRequestDecide])) {
    return (
      <chakra.span color="blue.300" fontWeight="medium">
        {t('awaitingApproval')}
      </chakra.span>
    );
  }

  const handleAccept = async () => {
    try {
      await decide({
        variables: { id: requestId, decision: ServiceAdminOrgAccessRequestDecision.Approved },
      });
      toast({ status: 'success', title: t('accessRequestAccepted') });
    } catch (err) {
      toast({
        status: 'error',
        title: t('accessRequestDecisionFailed'),
        description: fromError(err, 'DecideOrgAdminAccessRequest'),
      });
    }
  };

  return (
    <Button
      variant="link"
      colorScheme="green"
      fontWeight="medium"
      leftIcon={<SuccessIcon color="green.300" width="5" height="5" />}
      isDisabled={loading}
      onClick={handleAccept}
    >
      {t('accept')}
    </Button>
  );
}

function ReceivedRequestActions({ request }: { request: ReceivedAccessRequest }) {
  const toast = useToast();
  const { verifyUserPermissions } = useAuth();
  const [revoke] = useRevokeOrgAdminAccessRequestMutation({
    refetchQueries: ['ReceivedAccessRequests'],
    awaitRefetchQueries: true,
  });

  const revokeAction = useDestructiveAction<ReceivedAccessRequest>({
    title: t('revokeAccess'),
    message: (target) => (
      <Trans
        i18nKey="revokeAccessConfirm"
        values={{ name: target.requesterName }}
        components={{ strong: <strong /> }}
      />
    ),
    confirmLabel: t('revoke'),
    onConfirm: async (target) => {
      try {
        await revoke({ variables: { id: target.id } });
        toast({ status: 'success', title: t('accessRevoked') });
      } catch (err) {
        toast({
          status: 'error',
          title: t('revokeAccessFailed'),
          description: fromError(err, 'RevokeOrgAdminAccessRequest', {
            SERVICE_ADMIN_ORG_ACCESS_REQUEST_NOT_ACTIVE: t('accessRequestNotActive'),
          }),
        });
      }
    },
  });

  if (
    getEffectiveStatus(request) !== ServiceAdminOrgAccessRequestStatus.Approved ||
    !verifyUserPermissions([Permission.ServiceAdminOrgAccessRequestDecide])
  ) {
    return null;
  }

  return (
    <>
      <Menu placement="bottom-end">
        <MenuButton
          as={IconButton}
          variant="unstyled"
          display="flex"
          alignItems="center"
          justifyContent="center"
          minWidth="8"
          height="8"
          aria-label={t('actions')}
          icon={<MoreIconVertical color="blue.400" width="5" height="5" />}
        />
        <MenuList>
          <MenuItem onClick={() => revokeAction.askConfirmation(request)}>
            {t('revokeAccess')}
          </MenuItem>
        </MenuList>
      </Menu>
      {revokeAction.confirmationNode}
    </>
  );
}

ReceivedAccessRequests.graphql = {
  queries: {
    ReceivedAccessRequests: gql`
      query ReceivedAccessRequests($filter: ServiceAdminOrgAccessRequestFilter) {
        organizationAccessRequests(filter: $filter) {
          id
          requestingServiceAdmin {
            givenName
            familyName
            email
          }
          requestedDurationMinutes
          status
          createdAt
          accessExpiresAt
        }
      }
    `,
  },
  mutations: {
    DecideOrgAdminAccessRequest: gql`
      mutation DecideOrgAdminAccessRequest(
        $id: ID!
        $decision: ServiceAdminOrgAccessRequestDecision!
      ) {
        decideOrgAdminAccessRequest(id: $id, decision: $decision) {
          id
          status
          accessExpiresAt
          decidedAt
        }
      }
    `,
    RevokeOrgAdminAccessRequest: gql`
      mutation RevokeOrgAdminAccessRequest($id: ID!) {
        revokeOrgAdminAccessRequest(id: $id) {
          id
          status
        }
      }
    `,
  },
};
